// Outbound webhook URL guard (spec 11 §6): https, default port, no credentials, public addresses only.
// The syntax check runs at creation (SQL + UI); DNS is resolved again before EVERY delivery.
// Decimal / octal / hex IPv4 spellings are normalised to dotted quads by the WHATWG URL parser before
// the checks below run, so they are classified like any other literal.
export type UrlCheck =
  | { ok: true; url: URL }
  | {
      ok: false;
      code:
        | "invalid_url"
        | "https_required"
        | "credentials_not_allowed"
        | "port_not_allowed"
        | "host_not_allowed"
        | "private_address"
        | "unresolvable";
    };
export type ResolveOpts = { signal?: AbortSignal };
export type Resolver = (
  hostname: string,
  opts?: ResolveOpts,
) => Promise<string[]>;
export type DnsLookup = (
  hostname: string,
  type: "A" | "AAAA",
  opts?: ResolveOpts,
) => Promise<string[]>;

/**
 * Resolver over both address families (Task 18 passes Deno.resolveDns). A "no data" answer (Deno:
 * NotFound) on one family is an empty list; any other error fails closed, because an unchecked family
 * could still be dialled by fetch.
 */
export function dualStackResolver(lookup: DnsLookup): Resolver {
  const family = (host: string, type: "A" | "AAAA", opts?: ResolveOpts) =>
    lookup(host, type, opts).catch((e: unknown) => {
      if ((e as Error)?.name === "NotFound") return [] as string[];
      throw e;
    });
  return async (host, opts) => {
    const [a, aaaa] = await Promise.all([
      family(host, "A", opts),
      family(host, "AAAA", opts),
    ]);
    return [...a, ...aaaa];
  };
}

/** Rejects as soon as the signal aborts, even if the resolver ignores it. */
function untilAborted<T>(p: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return p;
  if (signal.aborted) return Promise.reject(new Error("aborted"));
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new Error("aborted"));
    signal.addEventListener("abort", onAbort, { once: true });
    p.then(resolve, reject).finally(() =>
      signal.removeEventListener("abort", onAbort),
    );
  });
}

const BLOCKED_SUFFIXES = [
  ".localhost",
  ".local",
  ".internal",
  ".home.arpa",
  ".lan",
];
const V4_BLOCKS: readonly [string, number][] = [
  ["0.0.0.0", 8],
  ["10.0.0.0", 8],
  ["100.64.0.0", 10],
  ["127.0.0.0", 8],
  ["169.254.0.0", 16],
  ["172.16.0.0", 12],
  ["192.0.0.0", 24],
  ["192.0.2.0", 24],
  ["192.88.99.0", 24],
  ["192.168.0.0", 16],
  ["198.18.0.0", 15],
  ["198.51.100.0", 24],
  ["203.0.113.0", 24],
  ["224.0.0.0", 4],
  ["240.0.0.0", 4],
];

function v4ToInt(ip: string): number | null {
  const parts = ip.split(".");
  if (parts.length !== 4) return null;
  let n = 0;
  for (const p of parts) {
    if (!/^\d{1,3}$/.test(p) || Number(p) > 255) return null;
    n = n * 256 + Number(p);
  }
  return n;
}

function isPrivateV4(ip: string): boolean {
  const n = v4ToInt(ip);
  if (n === null) return true; // fail closed on anything that is not a strict dotted quad
  return V4_BLOCKS.some(([base, bits]) => {
    const b = v4ToInt(base)!;
    return n >= b && n < b + 2 ** (32 - bits);
  });
}

function expandV6(input: string): number[] | null {
  let s = input.toLowerCase();
  const tail: number[] = [];
  const v4 = /^(.*:)(\d{1,3}(?:\.\d{1,3}){3})$/.exec(s);
  if (v4) {
    const n = v4ToInt(v4[2]);
    if (n === null) return null;
    tail.push(Math.floor(n / 65536), n % 65536);
    s = v4[1].endsWith("::") ? v4[1] : v4[1].slice(0, -1);
  }
  const halves = s.split("::");
  if (halves.length > 2) return null;
  const parse = (part: string) =>
    (part ? part.split(":") : []).map((x) =>
      /^[0-9a-f]{1,4}$/.test(x) ? parseInt(x, 16) : NaN,
    );
  const head = parse(halves[0]);
  const rest = halves.length === 2 ? parse(halves[1]) : [];
  const zeros =
    halves.length === 2 ? 8 - tail.length - head.length - rest.length : 0;
  if (halves.length === 2 ? zeros < 1 : head.length + tail.length !== 8)
    return null;
  const all = [...head, ...Array<number>(zeros).fill(0), ...rest, ...tail];
  return all.length === 8 &&
    all.every((x) => Number.isInteger(x) && x >= 0 && x <= 0xffff)
    ? all
    : null;
}

const v4Of = (hi: number, lo: number) =>
  `${hi >> 8}.${hi & 255}.${lo >> 8}.${lo & 255}`;

function isPrivateV6(ip: string): boolean {
  const h = expandV6(ip.replace(/^\[|\]$/g, "").split("%")[0]);
  if (!h) return true; // fail closed
  const zeroUpTo = (k: number) => h.slice(0, k).every((x) => x === 0);
  if (zeroUpTo(5) && h[5] === 0xffff) return isPrivateV4(v4Of(h[6], h[7])); // ::ffff:a.b.c.d (mapped)
  if (zeroUpTo(4) && h[4] === 0xffff && h[5] === 0)
    return isPrivateV4(v4Of(h[6], h[7])); // ::ffff:0:a.b.c.d
  if (h[0] === 0x64 && h[1] === 0xff9b && h.slice(2, 6).every((x) => x === 0))
    return isPrivateV4(v4Of(h[6], h[7])); // NAT64
  if (h[0] === 0x2002) return isPrivateV4(v4Of(h[1], h[2])); // 6to4 embeds an IPv4 address
  // Allow-list: only global unicast 2000::/3 can be public; inside it, block the special ranges.
  if ((h[0] & 0xe000) !== 0x2000) return true; // ::/8, 100::/64, fc00::/7, fe80::/10, fec0::/10, ff00::/8, 64:ff9b:1::/48 ...
  if (h[0] === 0x2001 && h[1] === 0x0000) return true; // 2001::/32 Teredo
  if (h[0] === 0x2001 && h[1] === 0x0db8) return true; // 2001:db8::/32 documentation
  return h[0] === 0x3fff && (h[1] & 0xf000) === 0; // 3fff::/20 documentation (RFC 9637)
}

export const isPrivateAddress = (ip: string): boolean =>
  ip.includes(":") ? isPrivateV6(ip) : isPrivateV4(ip);
/** Lower-cased host without any trailing dots ("localhost.." → "localhost"). */
const normalizeHost = (hostname: string) =>
  hostname.toLowerCase().replace(/\.+$/, "");
const isIpLiteral = (host: string) =>
  host.startsWith("[") || /^\d{1,3}(?:\.\d{1,3}){3}$/.test(host);

export function checkWebhookUrlSyntax(raw: string): UrlCheck {
  if (typeof raw !== "string" || raw.length > 2000)
    return { ok: false, code: "invalid_url" };
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return { ok: false, code: "invalid_url" };
  }
  if (url.protocol !== "https:") return { ok: false, code: "https_required" };
  if (url.username || url.password)
    return { ok: false, code: "credentials_not_allowed" };
  if (url.port && url.port !== "443")
    return { ok: false, code: "port_not_allowed" };
  const host = normalizeHost(url.hostname);
  if (isIpLiteral(host))
    return isPrivateAddress(host)
      ? { ok: false, code: "private_address" }
      : { ok: true, url };
  if (
    !host.includes(".") ||
    host.split(".").some((label) => label === "") ||
    host === "localhost" ||
    BLOCKED_SUFFIXES.some((s) => host.endsWith(s))
  )
    return { ok: false, code: "host_not_allowed" };
  return { ok: true, url };
}

export async function checkWebhookUrl(
  raw: string,
  resolve: Resolver,
  opts: ResolveOpts = {},
): Promise<UrlCheck> {
  const syntax = checkWebhookUrlSyntax(raw);
  if (!syntax.ok || isIpLiteral(syntax.url.hostname)) return syntax;
  let addresses: string[];
  try {
    addresses = await untilAborted(
      resolve(normalizeHost(syntax.url.hostname), opts),
      opts.signal,
    );
  } catch {
    return { ok: false, code: "unresolvable" };
  }
  if (addresses.length === 0) return { ok: false, code: "unresolvable" };
  return addresses.some(isPrivateAddress)
    ? { ok: false, code: "private_address" }
    : syntax;
}
